import { IResponse } from "./index";
import {
  IAppointmentProgram,
  IDatedText,
  ITherapist,
  ITherapistDocument,
  IUser,
  TNamedItem,
} from "../../types/types";
import { IInquiry } from "../../utils/inquirues";
import { IServerStatus } from "../../utils/therapistDocuments";
import { INumberOption } from "../../utils/constants";

export type TUploadResponse = {
  success: boolean;
  files: string[]; // Changed from 'urls' to match backend response
  message?: string;
};
export type TQuestionnaire = {
  Id: number;
  Desc: string;
  Symbol: string;
  StartQuestion: string;
  StatusId: number;
  FactorQuestionnaire: number;
  IsLink: boolean | null;
  QuestionnaireCompaniesId: number;
  qcsuu: number;
  TotalAmount?: number | null;
  TotalAmountPay?: number | null;
  CompanyServiceId?: number | null;
  QuestionnaireCompaniesScoreUsersStatuses?: string | null;
};
export type TQuestionnaireCompanies = {
  Id: number;
  CompanyId: number;
  QuestionnaireCompaniesHeadersId: number;
  QuestionnaireCompaniesTypeId: number;
  StatusId: Number;
  TherapistQuestionnaireId: number;
  UserQuestionnaireId: number;
  Dayly: number | null;
  Monthly: number | null;
  Weekly: number | null;
};

export type TQuestionnaireQuestion = {
  Id: number;
  QuestionnaireId: number;
  DataTypesId: number;
  Desc: string;
  NextQuestionId: number | null;
  StatusId: number;
  Seq: number;
  DescDateTypes?: string;
  Answer?: string | null;
  Score: number;
  OptionsCountMax: number | null;
  OptionsCountMin: number | null;
  IsNoScore: boolean | null;
  CompanyGroupsRRMHeadersId: number | null;
  SpecificTypeId?: number;
  FactorQuestion: number;
};
export type TQuestionnaireGroup = {
  [key: string]: TQuestionnaireQuestion[];
};
export type TTherapistState = ITherapist & {
  mainPageUpdates: IDatedText[];
  inquiries: IInquiry[];
  users: TTherapistUser[];
  documents?: ITherapistDocument[];
  actionReportQuestions: TQuestionnaireQuestion[];
};
export type TUpdateUserRequestBody = {
  op: string;
  path: string;
  value: string;
};
export type TMainPageUpdatesResponse = IResponse & {
  updates: IDatedText[];
};
export type TInquiriesResponse = IResponse & {
  inquiries: IInquiry[];
  myInquiries: IInquiry[];
};

export type TUpdateUserRequest = {
  guid: string | number;
  body: TUpdateUserRequestBody[];
};
export type TUpdateUserSettingsRequest = {
  id: string;
  body: TUpdateUserSettingsRequestBody;
};

export type TUpdateUserSettingsRequestBody = {
  sendToEmail: boolean;
  sendAds: boolean;
  sendNotifications: boolean;
};
export type TUpdateUserDocumentStatusRequest = {
  id: string;
  documentId: string;
  status: string;
};

export type TGetUserDocumentsResponse = IResponse & {
  documents: ITherapistDocument[];
};

export type TMakeAppointmentRequest = {
  therapistId: string;
  date: Date;
};

export interface IFile {
  lastModified?: number;
  lastModifiedDate?: Date;
  name: string;
  size: number;
  type: string;
  webkitRelativePath: string;
}
export interface IQuestionAnswer {
  Question: string;
  Answer: string;
}
export type TUsersWithOutTherapist = {
  num: number;
  questionnaireCompaniesId: number;
  cycle: number;
  timestamp: string;
  questionnaireCompaniesTypeId: number;
  questionnaireCompaniesType: string;
  usersId: number;
  companyId: number;
  fakeCompanyId: number | null;
  fakeCompanyName: string | null;
  firstName: string | null;
  fullName?: string | null;
  lastName: string | null;
  usersName: string | null;
  phone: string;
  email: string | null;
  userQuestionnaireId: number;
  therapistQuestionnaireId: number;
  generalRRMSEQ: number | null;
  specificFields: string;
  status: number;
  comments: string | null;
  birth: string | null;
  guid: string;
  therapistCycle: number | null;
  appointmentsId: number | null;
  appointmentsTypeId: number | null;
  appointmentsActive: boolean | null;
  therapistActionId: number | null;
  therapistsUsersId: number | null;
};

export type TGroupedUsersWithoutTherapistByFakeCompanyId = Record<
  "null" | string,
  TUsersWithOutTherapist[]
>;

export type TTherapistWithUsers = TUsersWithOutTherapist & {
  therapistsId: number;
  files: string;
};

export type TMainPageInfo = {
  therapistWithUsers: TTherapistWithUsers[];
  usersWithOutTherapist: TUsersWithOutTherapist[];
};
export type TTakeInquiryRequest = {
  Id: number;
  UsersGuid: string;
  TherapistsGuid: string;
  QuestionnaireId: number;
  Cycle: number;
  Status: number;
  QuestionnaireCompaniesId: number;
};

export type TGetQuestionsReportByUserId = {
  questionnaireId: number;
  userId: string;
  cycle: number;
};
export type TQuestionnaireData = {
  Questionnaire: TQuestionnaire[];
  Questions: TQuestionnaireQuestion[];
};
export type TGroup = {
  Id: number;
  Name: string;
  QuestionsId: number;
  ParentGroupId?: number | null;
  ParentGroup?: TGroup | null;
  OptionsCountMin?: number | null;
  OptionsCountMax?: number | null;
};
export type TQuestionOption = {
  Id: number;
  QuestionsId: number;
  GroupId: number | null;
  Groups: TGroup | null;
  Desc: string;
  NextQuestionId: number | null;
  NextQuestions: any | null; // Adjust the type of NextQuestions if you know the specific structure or replace 'any' with more specific type
  Seq: number;
  count?: number | null;
  min?: null | number;
};
export type TGetAllQuestionsAndOptionsResponse = {
  Questions: TQuestionnaireQuestion;
  QuestionsOptions: TQuestionOption[];
  // optional field returned by server for special question types (e.g. ICD)
  SpecificTypeId?: number;
}[];

export type TDefaultReportData = {
  QuestionnaireCompaniesId: number;
  QuestionnaireId: number;
  Cycle: number | null;
  DefaultName: string;
  AllowTherapistToSeeAiAnalysis?: number;
};

export type TDefaultReportDataResponse = {
  DefaultReportData: TDefaultReportData[];
};

export type TInsertAnswerRequest = {
  Id: string | number;
  QuestionnaireId: string;
  QuestionsId: string;
  UsersId: string | number;
  Answer: string;
};
export type TInsertAllAnswersRequest = TInsertAnswerRequest & {
  AnswerDate: Date | null;
  QuestionsOptionsIds: string;
  Cycle: number | null;
  UsersId?: number;
};

export type TInsertAllAnswersQuestionsResponse = {
  success: boolean;
  userguid: string;
  questionnaireCompaniesScoreUsersId: number;
  processing: boolean;
};

export type IcdAnalysisResult = {
  success: boolean;
  isValid: boolean;
  explanation: string;
  suggestedCodes: string[];
  rawResponse?: string;
};

// Issue item from AI analysis
export type TIssueItem = {
  id: string;
  title: string;
  severity: string; // Low | Moderate | High
  short: string;
  details: string;
  fixes: string[];
};

// Analysis metadata
export type TAnalysisMeta = {
  language?: string;
  totalTokens?: number;
  generatedAt?: string;
};

// Full report analysis result (SimplifiedReportResult from server)
export type IFullReportAnalysisResult = {
  success: boolean;
  isValid: boolean;
  summary: string;
  risk: string; // None | Low | Moderate | High
  suggestions: string[];
  issues: TIssueItem[];
  meta?: TAnalysisMeta;
};

export type TQuestionnaireEntry = {
  Cycle: number;
  QuestionnaireId: number;
  Desc: string;
  QuestionnaireCompaniesId: number;
  TherapistsId: number | null;
  Timestamp: string;
  IsAccessible: boolean;
  TherapistName?: string;
  TotalAmountPay?: number | null;
  TotalAmount?: number | null;
};

export type TAllUserQuestionnairesData = {
  Questionnaires?: TQuestionnaireEntry[];
  QuestionnairesTherapists: TQuestionnaireEntry[];
  QuestionnairesUsers: TQuestionnaireEntry[];
};
export type TGetMultipleQuestionnairesByUserIdAndCycleRequest = {
  UsersGuidId: string;
  Cycle: number;
  QuestionnaireCompaniesId: number;
};
export type TCompanyGroupsRRMEntry = {
  Id: number;
  CompanyId?: number;
  Seq: number;
  QuestionnaireId?: number;
  CompanyGroupsRRMHeadersId?: number;
  Title: string;
  Desc: string | null;
  Color: string;
  Anliza: string | null;
  Recommendations: string | null;
  Highlights: string | null;
  Conversation: string | null;
  Start: number;
  End: number;
  StatusId: number;
};

export type TRRMDisplayEntry = {
  id: number;
  rrmseq: number;
  color: string;
  desc: string | null;
  title: string;
  anliza: string | null;
  recommendations: string | null;
  highlights: string | null;
  conversation: string | null;
  statusId: number;
  rangeStart?: number | null;
  rangeEnd?: number | null;
};

export type THeaderScore = {
  CompanyGroupsRRMHeadersId: number;
  Desc: string;
  Score: number;
  CompanyGroupsRRM?: TCompanyGroupsRRMEntry[] | null;
};

 

  // Type for questionnaire score entries
  export type TQuestionnaireScore = {
    QuestionnaireCompaniesId: number;
    QuestionnaireId: number;
    Cycle: number;
    TherapistsId: number;
    TherapistsName: string | null;
    Score: number;
    Name: string;
    RRM: number;
    HeaderScores?: THeaderScore[] | null;
  };

// Type for total MRR score
export type TTotalScoreRRM = {
  TotalScore: number;
  RRM: number;
};
export type TRRMListEntry = {
  anliza: string | null;
  color: string;
  companyId: number;
  conversation: string | null;
  desc: string | null;
  highlights: string | null;
  id: number;
  recommendations: string | null;
  rrmseq: number;
  statusId: number;
  title: string;
};

export type TQuestionnaireContainer = {
  FakeCompanyId?: number | null;
  Questionnaire: TQuestionnaire;
  Questions: TQuestionnaireQuestion[];
  QuestionnaireCompanies: TQuestionnaireCompanies;
  TotalAmountPay?: number | null;
  TotalAmount?: number | null;
  CompanyServiceId: number | null;
  CompanyServiceName: string | null;
  QuestionnaireCompaniesScoreUsersStatuses?: string | null;
  QuestionnaireCompaniesScoreUsersStatusesId?: number | null;
};
export type TMessage = {
  Id: number;
  QuestionnaireIds: string;
  Score: number;
  MessagesId: number;
};
export type TGetMultipleQuestionnairesByUserIdAndCycleResponse = {
  TherapistQuestionnaire: TQuestionnaireContainer;
  UserQuestionnaire: TQuestionnaireContainer;
  User: TTherapistUser;
  RRMList: TRRMListEntry[];
  RRMAndScore?: TQuestionnaireScore[];
};
export type TAddMessageRequest = {
  QuestionnaireIds: string;
  MessageId: string;
  Title: string;
  Desc: string;
  Score: string;
  UsersId: string;
  Cycle: number | null;
};
export type TGetStatusesResponse = IServerStatus[];


export interface IMedia {
  Id: number;
  Name: string;
  NameEn: string;
}
export type TMediaResponse = IMedia[];

export type TTherapistUser = {
  Id: number;
  Guid: string;
  FirstName: string;
  LastName: string | null;
  fullName: string;
  roleId: number;
  Account: string | null;
  Password: string | null;
  Phone: string;
  Email: string | null;
  Birth: string | null;
  Passport: string | null;
  TempCode: string | null;
  Avatar: string | null;
  Address: string | null;
  PlanId: number;
  GenderId: number | null;
  StatusId: number;
  DateRegister: string | null;
  UsersFlowId: string | null;
  FakeCompanyId?: null | number;
  TreatmentGroupId?: null | number;
  TreatmentGroupName?: null | string;

};
export type TClinicSummaryUser = {
  Users: {
    FirstName: string;
    LastName: string;
    Guid: string;
    Id: number;
    DateRegister: string;
    Phone: string;
    Birth: string;
    Passport: string;
    Address: string;
    Account: string;
    GenderId: number;
    StatusId: number;
    FakeCompanyId: number;
  };
};

/** `location.state` for `/users/update` (EditDataPage): patient row from the therapist users list (API PascalCase). */
export type TEditDataPageLocationState = {
  Address?: string | null;
  Avatar?: string | null;
  Birth: string | null;
  CityId?: number;
  CompanyId?: number;
  DateRegister: string | null;
  Email?: string | null;
  FakeCompanyId?: number | null;
  FirstName: string;
  FullName?: string;
  GenderId?: number | null;
  Guid: string;
  Id: number;
  LastName: string | null;
  MigzarId?: number | null;
  Passport: string | null;
  Phone: string;
  QuestionnaireCompaniesHeadersId?: number;
  RoleId?: number;
  StatusId: number;
  UserName: string;
  UsersId: number;
  treatmentGroupId?: number;
};

export type TUserInfo = {
  Phone: string;
  UsersId: number;
  FirstName: string | null;
  LastName: string | null;
  FullName?: string | null;
  Birth: string | null;
};
export type TGetAllTherapistUsersResponse = {
  TherapistUsers: TTherapistUser[];
};
export interface IGenderOption {
  id: number;
  name: string;
}
export type TTakeUserRequest = {
  TherapistsId: number | string;
  Id: string;
  FirstName: string;
  Phone?: string;
  LastName?: string;
  Email?: string;
  Birth?: Date;
  GenderId: number;
  Passport?: string;
  [key: string]: any;
};

export interface IMedia {
  id: number;
  name: string;
}
export interface IMigzar {
  id: number;
  name: string;
}
export type TMigzarResponse = IMigzar[];

export type TGenderResponse = IGender[];
export interface IGender {
  id: number;
  name: string;
}

  export interface IChatAppointment {
    active: number | null | boolean;
    appointmentDate: string;
    appointmentsProgramsId: string | null;
    appointmentReport: string | null;
    appointmentsTypeId: number | null;
    companyId: number | null;
    comments: string | null;
    endTime: string | null;
    id: number;
    mediaId: number;
    statusId: number | null;
    startTime: string | null;
    therapistsId: number;
    usersId: number;
    online?: boolean;
    users: IUser;
    key: string;
  }
  export type TPendingReport = {
    appointment: IChatAppointment;
    userName: string | null;
  }
  export type TPendingInquiry = {
    num: number;
    usersId: number;
    userName: string;
  
    firstName: string;
    lastName: string;
    phone: string;
    birth: string; // ISO date string
    guid: string;
  
    therapistId: number;
    therapistName: string;
    therapistQuestionnaireId: number;
  
    questionnaireCompaniesId: number;
    questionnaireCompaniesTypeId: number;
    questionnaireCompaniesType: string;
  
    cycle: number;
    timestamp: string;
  };
  export interface IPendingReportResponce {
    notReported: TPendingInquiry[];
    notReportedTotal: number;
    pendingAppointments: TPendingReport[];
    pendingAppointmentsTotal: number;
  }
  export interface IChatAppointmentResponse {
    appointmentDate: Date | null;
    id: number;
    companyId: number;
    mediaId: number | null;
    statusId: number;
    therapistsId: number;
    usersId: number;
  }

export interface IPostChatAppointmentRequest {
  UsersGuid: string;
  TherapistsGuid: string;
  MediaId: string;
  AppointmentDate: string;
  AppointmentReport?: number | string;
  Active: number | null;
  CompanyId: number;
  StatusId: number | null;
  StartTime?: string;
}
export type TUpdateChatAppointmentEntry = {
  op: "replace";
  path: string;
  value: string | boolean | Date | number;
};
export type TQuestionnaireRequest = {
  Id: string;
  QuestionnaireId: number | string;
  UsersGuid: string;
  TherapistsGuid: string;
  Cycle: number;
  Comment: string;
  CreateOn: string;
  QuestionnaireCompaniesId: number;
};

export type TAppointmentProgramRequest = {
  id: number;
  usersGuid: string;
  therapistsGuid: string;
  appointmentsProgramsId: number;
  comment: string;
  createOn: string;
};

export type TAppointmentProgramUpdate = {
  id: number;
  usersId: number | string;
  therapistsId: number | string;
  comment: string;
  createOn: string;
  therapistName: string;
};

export type TParsedComment = {
  Id: number;
  QuestionnaireId?: number;
  UsersId: number;
  Cycle?: number;
  TherapistsId: number;
  CreateOn: string | null;
  Comment: string;
  TherapistName: string;
};
export type TVideoDiagnostics = {
  diagnostics: {
    value: {
      reply: string;
    };
  };
};
export type TAnalyzeAudio = {
  optionsForReport: {
    value: {
      reply: string;
    };
  };
  meetingSummary: {
    value: {
      reply: string;
    };
  };
  conversationTranscription: string;
  textAnalysis: {
    value: {
      response: {
        value: {
          reply: string;
        };
      };
    };
  };
  audioAnalysis: string;
};
export type TParsedTreatmnentPlanComment = {
  id: number;
  usersId: number;
  therapistsId: number;
  createOn: string | null;
  comment: string;
  therapistName: string;
};

export type TUserAppointment = {
  id: number;
  questionnaireCompaniesHeadersId?: number;
  guid: string;
  companyId: number;
  firstName: string;
  lastName?: string;
  roleId: number;
  genderId?: number;
  phone: number;
  statusId: number;
  fullName: string;
  email?: string;
};

export type TCalendarAppointment = {
  appointments: TAppointment;
  isReport: boolean;
};

export type TAppointment = {
  id: number;
  companyId: number;
  usersId: number | null;
  users: TUserAppointment | null;
  therapistsId: number;
  appointmentsTypeId?: number;
  mediaId?: number;
  appointmentDate: Date;
  startTime?: string;
  endTime?: string | null;
  appointmentReport?: string;
  comments?: string;
  statusId: number;
  isReport?: boolean;
  // Google Calendar specific fields
  source?: 'database' | 'google_calendar' | 'bpreven';
  googleEventId?: string;
  googleEventSummary?: string;
  googleEventDescription?: string;
  googleEventLocation?: string;
  googleEventHtmlLink?: string;
  // Additional Google Calendar fields
  summary?: string;
  title?: string;
  description?: string;
  location?: string;
  start?: string;
  end?: string;
  treatmentGroupId?: number | null;
  treatmentGroupName?: string | null;
};

export type TAppointmentsProgramsScheduleResponse = {
  appointments: TAppointment;
  isReport: boolean;
};

export type TCreateAppointmentRequest = {
  AppointmentsProgramsId?: number;
  CompanyId: number;
  UsersId?: number;
  TherapistsId?: number | string;
  UsersGuid?: string;
  TherapistsGuid?: string;
  AppointmentDate?: Date;
  StatusId?: number;
};

export type TSupportMessage = {
  id: string | number;
  companyId: number;
  screenType: number;
  subject: string;
  body: string;
};

export interface ICompany {
  Id: number;
  Name: string;
  Guid: string | null;
}
export type TCompanyLabeledOption = ICompany & INumberOption;
export interface IRecommendation {
  Id: number;
  QuestionsId: number;
  GroupId: number | null;
  Groups: string | null;
  Desc: string;
  NextQuestionId: number | null;
  NextQuestions: string | null;
  Seq: number | null;
  FactorAnswer: number | null;
}

export type TChangeAppointmentByCalendarRequest = IAppointmentProgram & {
  replaceDate: string;
  replaceStartTime: string;
};

export type TUnSeenAppointmentsProgram = {
  id: number;
  usersId: number;
  type: number;
  countOfNotRead: number;
};

export type TConsentStatusResponse = {
  hasConsent: boolean;
  hasPendingRequest: boolean;
  hasRejectedRequest: boolean;
};
export interface ISpecializationArea {
  id: number;
  value: number;
  name: string;
  label: string;
  nameEn: string;
}
export type TSpecialization = {
  id: number;
  name: string;
  //  nameEn: string;
  // type: string;
  therapistsSpecializationsTypesId: number;
  statusId: number;
};

export type TMultyCompanyDetails = {
  CompanyId: number;
  CompanyName: string | null;
  CompanyNameEn: string | null;
  UserId: number;
  ImageUrl: string | null;
}

export type TTherapistSpecialization = {
  id: number;
  statusId: number;
  therapistsSpecializationsId: number;
  usersId: number;
};
// Voice Analysis Emotion Group (from Hume AI)
export interface TVoiceEmotionGroup {
  [emotion: string]: number;
}

// Voice Analysis Statistics (calculated from transcript)
export interface TVoiceAnalysisStatistics {
  word_count: number;
  sentence_count: number;
  speech_rate: number; // words per minute
  duration_seconds: number;
  pause_count: number;
  speaker_ratio: Record<string, number>; // speaker name -> percentage
}

// Voice Analysis Acoustic Insight
export interface TVoiceAnalysisInsight {
  title: string;
  text: string;
  icon?: string; // emoji
}

// Voice Analysis Anxiety Indicators
export interface TVoiceAnalysisAnxiety {
  level: "None" | "Low" | "Moderate" | "High";
  description: string;
}

// Voice Analysis LLM Insights
export interface TVoiceAnalysisLLMInsights {
  acoustic_insights?: TVoiceAnalysisInsight[];
  emotional_tone?: string; // "Positive" | "Neutral" | "Negative" | "Mixed"
  energy_level?: string; // "High" | "Medium" | "Low"
  communication_quality?: string;
  anxiety_indicators?: TVoiceAnalysisAnxiety;
}

// Enhanced Voice Analysis (new format with statistics + insights)
export interface TVoiceAnalysisEnhanced {
  // Original Hume AI emotion data
  emotions?: {
    group1?: TVoiceEmotionGroup;
    group2?: TVoiceEmotionGroup;
    group3?: TVoiceEmotionGroup;
    [key: string]: TVoiceEmotionGroup | undefined;
  } & {
    overall?: {
      positive: number;
      negative: number;
    };
    stress?: number;
  };

  // Calculated statistics
  statistics?: TVoiceAnalysisStatistics;

  // LLM-generated insights
  insights?: TVoiceAnalysisLLMInsights;
}

// Legacy Voice Analysis type (for backward compatibility)
export type TVoiceAnalysis = {
  group1?: {
    Calmness?: number;
    Concentration?: number;
    Contemplation?: number;
    Determination?: number;
    Excitement?: number;
    Interest?: number;
    [emotion: string]: number | undefined;
  };
  group2?: TVoiceEmotionGroup;
  group3?: TVoiceEmotionGroup;
  [key: string]: any;
  overall?: {
    positive: number;
    negative: number;
  };
  stress?: number;
  // Enhanced fields (optional for backward compatibility)
  statistics?: TVoiceAnalysisStatistics;
  insights?: TVoiceAnalysisLLMInsights;
};

export type TAnalysisData = {
  documentAnalysis: TDocumentAnalysis;
  videoAnalysis: string;
  voiceAnalysis: TVoiceAnalysis | string;
  textAnalysis: TTextAnalysis;
  documentAnalysisError: string | null;
  textAnalysisError: string | null;
  voiceAnalysisError: string | null;
};

export type TDocumentAnalysis = {
  diagnosis: string;
  medications: string;
  summary: string;
};

// Text Analysis Statistics
export interface TTextAnalysisStatistics {
  word_count: number;
  sentence_count: number;
  medical_terms_count: number;
  readability_score: number; // 1-10 scale
}

// Main Topic from text analysis
export interface TTextAnalysisTopic {
  title: string;
  description: string;
  icon?: string; // emoji
}

// Insight from text analysis
export interface TTextAnalysisInsight {
  title: string;
  text: string;
  icon?: string; // emoji: ✅, ⚠️, 📈, 🎯
}

// Risk Assessment
export interface TTextAnalysisRiskAssessment {
  level: "None" | "Low" | "Moderate" | "High";
  justification: string;
}

// Sentiment Analysis
export interface TTextAnalysisSentiment {
  overall: {
    positive: number;
    negative: number;
  };
  sadness_percent: number;
  fear_percent: number;
  anger_percent: number;
  joy_percent: number;
  happiness_percent: number;
  anticipation_percent: number;
  disgust_percent: number;
  surprise_percent: number;
  other_percent: number;
}

export interface TTextAnalysis {
  // New enhanced fields
  statistics?: TTextAnalysisStatistics;
  keywords?: string[];
  main_topics?: TTextAnalysisTopic[];
  insights?: TTextAnalysisInsight[];

  // Existing fields (kept for backward compatibility)
  clinical_flags?: string[];
  key_observations?: string[];
  linguistic_patterns?: string[];
  suggested_clinical_track?: string;
  risk_assessment?: TTextAnalysisRiskAssessment;
  scope_note?: string;
  sentiment_analysis?: TTextAnalysisSentiment;
}

export interface TTakeAllInquiriesOfTypeRequest {
  UsersGuid: string;
  TherapistsGuid: string;
  QuestionnaireId: number;
  Status: number;
  QuestionnaireCompaniesId: number;
}
// Status constants for meeting records
export const MeetingRecordStatus = {
  PROCESSING: 'processing',
  DRAFT: 'draft',
  COMPLETE: 'complete',
  FAILED: 'failed',
} as const;

export type TMeetingRecordStatus = typeof MeetingRecordStatus[keyof typeof MeetingRecordStatus];

// Processing stages for the stepper UI (must match backend ProcessingStages)
export const ProcessingStages = {
  UPLOADING: 'uploading',
  TRANSCRIBING: 'transcribing',
  ANALYZING: 'analyzing',
  FINALIZING: 'finalizing',
  // Legacy stage names (backward compatibility with existing records)
  TEXT_ANALYSIS: 'analyzing_text',
  AUDIO_ANALYSIS: 'analyzing_audio',
  SUMMARIZATION: 'summarizing',
} as const;

export type TProcessingStage = typeof ProcessingStages[keyof typeof ProcessingStages];

// Mapping of stages to step numbers for the stepper (4 total steps)
export const ProcessingStageSteps: Record<TProcessingStage, number> = {
  [ProcessingStages.UPLOADING]: 1,
  [ProcessingStages.TRANSCRIBING]: 2,
  [ProcessingStages.ANALYZING]: 3,
  // Legacy stages map to step 3 for backward compatibility
  [ProcessingStages.TEXT_ANALYSIS]: 3,
  [ProcessingStages.AUDIO_ANALYSIS]: 3,
  [ProcessingStages.SUMMARIZATION]: 3,
  [ProcessingStages.FINALIZING]: 4,
};

  export type TMeetingRecord = {
    audioAnalyze?: string;
    date: string;
    meetingSummary: string;
    optionsForReport: string;
    recordNumber: number;
    textAnalyze?: string;
    therapistId: number;
    therapistName?: string;             // Therapist name from database
    transcription: string;
    type: string;
    userId: number;
    videoAnalyze?: string;
    // New fields for status workflow
    duration?: number | null;           // Duration in seconds
    status?: TMeetingRecordStatus;      // processing | draft | complete | failed
    time?: string | null;               // Time of the meeting (HH:mm format)
    subject?: string | null;            // Subject/title of the meeting
    completedAt?: string | null;        // ISO datetime when marked complete
    processingStage?: TProcessingStage | null;  // Current processing stage
    errorMessage?: string | null;       // Error message if failed
    retryCount?: number;                // Number of retry attempts
    audioFileUrl?: string | null;       // URL to the recording audio file for playback
    failedAtStage?: TProcessingStage | null;  // Stage where processing failed (for stage-aware retry)
    appointmentId?: number | null;        // Linked appointment ID (if recording was linked to a calendar appointment)
    appointmentStartTime?: string | null;  // Start time of the linked appointment (HH:mm)
    appointmentDate?: string | null;       // Date of the linked appointment (yyyy-MM-dd)
    /** Patient/user score row linked to this recording (not used when therapist score exists). */
    questionnaireCompaniesScoreUsersId?: number | null;
    /** Therapist action-report score row — when set, export-to-action-report is disabled. */
    therapistQuestionnaireCompaniesScoreUsersId?: number | null;
    /** Questionnaire cycle for the linked action report, when provided by the backend. */
    cycle?: number | null;
    /** Questionnaire company id for filtering action-report questionnaires in the modal. */
    questionnaireCompaniesId?: number | null;
  };

export type TGetUserMeetingRecordsResponse = {
  success: boolean;
  records: TMeetingRecord[];
  count: number;
};

export type TUpdateMeetingRecordAppointmentLinkRequest = {
  userGuid: string;
  recordNumber: number;
  appointmentId: number | null;
  confirmReplace?: boolean;
};

  export type TUpdateMeetingRecordAppointmentLinkResponse = {
    success: boolean;
    recordNumber: number;
    appointmentId: number | null;
    previousAppointmentId: number | null;
    unassignedRecordNumbers: number[];
    appointmentDate?: string | null;
    appointmentStartTime?: string | null;
    questionnaireCompaniesScoreUsersId?: number | null;
    cycle?: number | null;
    questionnaireCompaniesId?: number | null;
    therapistQuestionnaireCompaniesScoreUsersId?: number | null;
  };

  export type TMeetingRecordConflict = {
    userId: number;
    recordNumber: number;
    questionnaireCompaniesScoreUsersId?: number | null;
    cycle?: number | null;
    therapistQuestionnaireCompaniesScoreUsersId?: number | null;
  };

  export type TUpdateMeetingRecordAppointmentLink409Response = {
    conflictingRecords: TMeetingRecordConflict[];
  };

  export type TMeetingRecordStatusResponse = {
    success: boolean;
    questionnaireCompaniesScoreUsersId?: number | null;
    cycle?: number | null;
    questionnaireCompaniesId?: number | null;
    therapistQuestionnaireCompaniesScoreUsersId?: number | null;
    recordNumber?: number;
    status?: TMeetingRecordStatus;
    processingStage?: TProcessingStage | null;
    errorMessage?: string | null;
    message?: string;
  };

  export type TMeetingRecordsListResponse = {
    success: boolean;
    records: TMeetingRecord[];
    count: number;
  };

  export type TFinalizeStreamingUploadResponse = {
    success: boolean;
    userGuid?: string;
    recordNumber?: number;
    message?: string;
    questionnaireCompaniesScoreUsersId?: number | null;
    cycle?: number | null;
    questionnaireCompaniesId?: number | null;
    therapistQuestionnaireCompaniesScoreUsersId?: number | null;
  };

  // Response from startProcessing endpoint (async workflow)
  export type TStartProcessingResponse = {
    success: boolean;
    userId: number;
    recordNumber: number;
    status: string;
    message?: string;
    questionnaireCompaniesScoreUsersId?: number | null;
    cycle?: number | null;
    questionnaireCompaniesId?: number | null;
    therapistQuestionnaireCompaniesScoreUsersId?: number | null;
  };

  function pickOptionalNullableInt(
    item: Record<string, unknown>,
    ...keys: string[]
  ): number | null | undefined {
    for (const key of keys) {
      if (!(key in item)) continue;
      const v = item[key];
      if (v === undefined) continue;
      if (v === null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    }
    return undefined;
  }

  /** Maps a MeetingRecords / getAllRecording record DTO to TMeetingRecord. */
  export function mapMeetingRecordFromApi(item: Record<string, unknown>): TMeetingRecord {
    const therapistQuestionnaireCompaniesScoreUsersId = pickOptionalNullableInt(
      item,
      "therapistQuestionnaireCompaniesScoreUsersId"
    );
    return {
      audioAnalyze: item.audioAnalyze as string | undefined,
      date: String(item.date ?? ""),
      meetingSummary: String(item.meetingSummary ?? ""),
      optionsForReport: String(item.optionsForReport ?? ""),
      recordNumber: Number(item.recordNumber),
      textAnalyze: item.textAnalyze as string | undefined,
      therapistId: Number(item.therapistId ?? 0),
      therapistName: item.therapistName as string | undefined,
      transcription: String(item.transcription ?? ""),
      type: String(item.type ?? ""),
      userId: Number(item.userId ?? 0),
      videoAnalyze: (item.faceAnalyze ?? item.videoAnalyze) as string | undefined,
      duration: item.duration as number | null | undefined,
      status: (item.status as TMeetingRecord["status"]) || MeetingRecordStatus.COMPLETE,
      time: item.time as string | null | undefined,
      subject: item.subject as string | null | undefined,
      completedAt: item.completedAt as string | null | undefined,
      processingStage: item.processingStage as TProcessingStage | null | undefined,
      errorMessage: item.errorMessage as string | null | undefined,
      retryCount: Number(item.retryCount ?? 0),
      audioFileUrl: (item.audioFileUrl as string | null) ?? null,
      failedAtStage: (item.failedAtStage as TProcessingStage | null) ?? null,
      appointmentId: (item.appointmentId as number | null) ?? null,
      appointmentStartTime: (item.appointmentStartTime as string | null) ?? null,
      appointmentDate: (item.appointmentDate as string | null) ?? null,
      therapistQuestionnaireCompaniesScoreUsersId,
      questionnaireCompaniesScoreUsersId:
        therapistQuestionnaireCompaniesScoreUsersId != null
          ? undefined
          : pickOptionalNullableInt(item, "questionnaireCompaniesScoreUsersId"),
      cycle: pickOptionalNullableInt(item, "cycle", "Cycle"),
      questionnaireCompaniesId: pickOptionalNullableInt(
        item,
        "questionnaireCompaniesId"
      ),
    };
  }

// --- Clinical Findings (MeetingRecords/clinical-findings) ---

export type TClinicalRaisedBy = 'clinician' | 'client';
export type TClinicalClientResponse = 'answered' | 'not_answered';

export type TRiskConcern = {
  category: string | null;      // enum — localize
  basis: string | null;         // only set when category = impaired_judgment_or_reality_testing
  categoryLabel: string | null; // model's own label — stored, not rendered
  sourcePhrase: string | null;
  evidenceBasis: string | null;
  raisedBy: string | null;
  clientResponse: string | null;
};

export type TSuicidalityFinding = {
  recordNumber: number;
  value: number; // 0-3
  temporalContext: string | null;
  sourcePhrase: string | null;
  evidenceBasis: string | null;
  raisedBy: TClinicalRaisedBy | null;
  clientResponse: TClinicalClientResponse | null;
  absenceReason: string | null;
  statusLine: string | null;         // render exactly as returned, never translate
  statusLineLanguage: string | null;
  alert: boolean;
  textLanguage: string | null;
  extractionNotes: string | null;
  promptVersion: string | null;
  createdAt: string;
};

export type TRiskFinding = {
  recordNumber: number;
  value: number; // 0-2
  concerns: TRiskConcern[];
  absenceReason: string | null;
  absencePhrase: string | null;
  raisedBy: string | null;
  clientResponse: string | null;
  statusLine: string | null;
  statusLineLanguage: string | null;
  alert: boolean;
  textLanguage: string | null;
  extractionNotes: string | null;
  promptVersion: string | null;
  createdAt: string;
};

export type TMedicationMention = {
  verbatimPhrase: string | null;
  matchedIngredient: string | null; // often "not_sure" — don't build logic on it
  resolution: string | null;        // enum — localize
  temporalStatus: string | null;    // enum — localize; only when resolution = yes
  sourcePhrase: string | null;
  evidenceBasis: string | null;
  raisedBy: string | null;
  clientResponse: string | null;
  statusLine: string | null;        // render exactly as returned
};

export type TMedicationFlag = {
  mentionStatus: string | null; // not_mentioned | not_applicable | has_mentions
  mentions: TMedicationMention[];
  extractionNotes: string | null;
};

export type TMedicationFinding = {
  recordNumber: number;
  prescribed: TMedicationFlag;
  selfDirected: TMedicationFlag;
  statusLineLanguage: string | null;
  textLanguage: string | null;
  promptVersion: string | null;
  createdAt: string;
};

export type TRecommendationItem = {
  tier: number | null; // 1 = agreed, 2 = to be decided
  itemType: string | null;
  category: string | null;      // enum — localize; "other" shows categoryLabel
  categoryLabel: string | null;
  recommendationLine: string | null; // render exactly as returned
  sourcePhrase: string | null;
  evidenceBasis: string | null;
  raisedBy: string | null;
  clientResponse: string | null;
  suggestedNeed: string | null;
  suggestedNeedLabel: string | null;
};

export type TRecommendationsFinding = {
  recordNumber: number;
  status: string | null; // has_items | no_recommendations | cant_tell
  items: TRecommendationItem[];
  statusLine: string | null; // only set when there are no items
  statusLineLanguage: string | null;
  extractionNotes: string | null;
  textLanguage: string | null;
  promptVersion: string | null;
  createdAt: string;
};

export type TClinicalFindingsResponse = {
  latestSuicidality: TSuicidalityFinding | null;
  latestRisk: TRiskFinding | null;
  latestMedication: TMedicationFinding | null;
  latestRecommendations: TRecommendationsFinding | null;
  suicidalityHistory: TSuicidalityFinding[]; // newest first, includes latest
  riskHistory: TRiskFinding[];               // newest first, includes latest
  medicationHistory: TMedicationFinding[];   // newest first, includes latest
  recommendationsHistory: TRecommendationsFinding[]; // newest first, includes latest
};

/**
 * Field casing for this endpoint hasn't been confirmed against a live response
 * (PascalCase unless the API has a global camelCase policy), so every field is
 * read under either casing — same defensive approach as pickOptionalNullableInt
 * / mapMeetingRecordFromApi above.
 */
function pickField<T = unknown>(item: Record<string, unknown>, key: string): T | undefined {
  if (key in item) return item[key] as T;
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  if (pascalKey in item) return item[pascalKey] as T;
  return undefined;
}

function pickStringField(item: Record<string, unknown>, key: string): string | null {
  const v = pickField<string>(item, key);
  return v ?? null;
}

function pickBooleanField(item: Record<string, unknown>, key: string): boolean {
  return Boolean(pickField<boolean>(item, key));
}

function mapRiskConcernFromApi(item: Record<string, unknown>): TRiskConcern {
  return {
    category: pickStringField(item, "category"),
    basis: pickStringField(item, "basis"),
    categoryLabel: pickStringField(item, "categoryLabel"),
    sourcePhrase: pickStringField(item, "sourcePhrase"),
    evidenceBasis: pickStringField(item, "evidenceBasis"),
    raisedBy: pickStringField(item, "raisedBy"),
    clientResponse: pickStringField(item, "clientResponse"),
  };
}

export function mapSuicidalityFindingFromApi(item: Record<string, unknown>): TSuicidalityFinding {
  return {
    recordNumber: Number(pickField(item, "recordNumber")),
    value: Number(pickField(item, "value")),
    temporalContext: pickStringField(item, "temporalContext"),
    sourcePhrase: pickStringField(item, "sourcePhrase"),
    evidenceBasis: pickStringField(item, "evidenceBasis"),
    raisedBy: pickStringField(item, "raisedBy") as TClinicalRaisedBy | null,
    clientResponse: pickStringField(item, "clientResponse") as TClinicalClientResponse | null,
    absenceReason: pickStringField(item, "absenceReason"),
    statusLine: pickStringField(item, "statusLine"),
    statusLineLanguage: pickStringField(item, "statusLineLanguage"),
    alert: pickBooleanField(item, "alert"),
    textLanguage: pickStringField(item, "textLanguage"),
    extractionNotes: pickStringField(item, "extractionNotes"),
    promptVersion: pickStringField(item, "promptVersion"),
    createdAt: String(pickField(item, "createdAt") ?? ""),
  };
}

export function mapRiskFindingFromApi(item: Record<string, unknown>): TRiskFinding {
  const concerns = pickField<Record<string, unknown>[]>(item, "concerns") ?? [];
  return {
    recordNumber: Number(pickField(item, "recordNumber")),
    value: Number(pickField(item, "value")),
    concerns: Array.isArray(concerns) ? concerns.map(mapRiskConcernFromApi) : [],
    absenceReason: pickStringField(item, "absenceReason"),
    absencePhrase: pickStringField(item, "absencePhrase"),
    raisedBy: pickStringField(item, "raisedBy"),
    clientResponse: pickStringField(item, "clientResponse"),
    statusLine: pickStringField(item, "statusLine"),
    statusLineLanguage: pickStringField(item, "statusLineLanguage"),
    alert: pickBooleanField(item, "alert"),
    textLanguage: pickStringField(item, "textLanguage"),
    extractionNotes: pickStringField(item, "extractionNotes"),
    promptVersion: pickStringField(item, "promptVersion"),
    createdAt: String(pickField(item, "createdAt") ?? ""),
  };
}

function mapMedicationMentionFromApi(item: Record<string, unknown>): TMedicationMention {
  return {
    verbatimPhrase: pickStringField(item, "verbatimPhrase"),
    matchedIngredient: pickStringField(item, "matchedIngredient"),
    resolution: pickStringField(item, "resolution"),
    temporalStatus: pickStringField(item, "temporalStatus"),
    sourcePhrase: pickStringField(item, "sourcePhrase"),
    evidenceBasis: pickStringField(item, "evidenceBasis"),
    raisedBy: pickStringField(item, "raisedBy"),
    clientResponse: pickStringField(item, "clientResponse"),
    statusLine: pickStringField(item, "statusLine"),
  };
}

function mapMedicationFlagFromApi(item: Record<string, unknown> | undefined): TMedicationFlag {
  const flag = item ?? {};
  const mentions = pickField<Record<string, unknown>[]>(flag, "mentions") ?? [];
  return {
    mentionStatus: pickStringField(flag, "mentionStatus"),
    mentions: Array.isArray(mentions) ? mentions.map(mapMedicationMentionFromApi) : [],
    extractionNotes: pickStringField(flag, "extractionNotes"),
  };
}

export function mapMedicationFindingFromApi(item: Record<string, unknown>): TMedicationFinding {
  return {
    recordNumber: Number(pickField(item, "recordNumber")),
    prescribed: mapMedicationFlagFromApi(pickField(item, "prescribed")),
    selfDirected: mapMedicationFlagFromApi(pickField(item, "selfDirected")),
    statusLineLanguage: pickStringField(item, "statusLineLanguage"),
    textLanguage: pickStringField(item, "textLanguage"),
    promptVersion: pickStringField(item, "promptVersion"),
    createdAt: String(pickField(item, "createdAt") ?? ""),
  };
}

function mapRecommendationItemFromApi(item: Record<string, unknown>): TRecommendationItem {
  const tier = pickField(item, "tier");
  return {
    tier: tier == null ? null : Number(tier),
    itemType: pickStringField(item, "itemType"),
    category: pickStringField(item, "category"),
    categoryLabel: pickStringField(item, "categoryLabel"),
    recommendationLine: pickStringField(item, "recommendationLine"),
    sourcePhrase: pickStringField(item, "sourcePhrase"),
    evidenceBasis: pickStringField(item, "evidenceBasis"),
    raisedBy: pickStringField(item, "raisedBy"),
    clientResponse: pickStringField(item, "clientResponse"),
    suggestedNeed: pickStringField(item, "suggestedNeed"),
    suggestedNeedLabel: pickStringField(item, "suggestedNeedLabel"),
  };
}

export function mapRecommendationsFindingFromApi(item: Record<string, unknown>): TRecommendationsFinding {
  const items = pickField<Record<string, unknown>[]>(item, "items") ?? [];
  return {
    recordNumber: Number(pickField(item, "recordNumber")),
    status: pickStringField(item, "status"),
    items: Array.isArray(items) ? items.map(mapRecommendationItemFromApi) : [],
    statusLine: pickStringField(item, "statusLine"),
    statusLineLanguage: pickStringField(item, "statusLineLanguage"),
    extractionNotes: pickStringField(item, "extractionNotes"),
    textLanguage: pickStringField(item, "textLanguage"),
    promptVersion: pickStringField(item, "promptVersion"),
    createdAt: String(pickField(item, "createdAt") ?? ""),
  };
}

/** Maps a MeetingRecords/clinical-findings response to TClinicalFindingsResponse. */
export function mapClinicalFindingsFromApi(raw: Record<string, unknown>): TClinicalFindingsResponse {
  const latestSuicidality = pickField<Record<string, unknown>>(raw, "latestSuicidality");
  const latestRisk = pickField<Record<string, unknown>>(raw, "latestRisk");
  const latestMedication = pickField<Record<string, unknown>>(raw, "latestMedication");
  const latestRecommendations = pickField<Record<string, unknown>>(raw, "latestRecommendations");
  const suicidalityHistory = pickField<Record<string, unknown>[]>(raw, "suicidalityHistory") ?? [];
  const riskHistory = pickField<Record<string, unknown>[]>(raw, "riskHistory") ?? [];
  const medicationHistory = pickField<Record<string, unknown>[]>(raw, "medicationHistory") ?? [];
  const recommendationsHistory = pickField<Record<string, unknown>[]>(raw, "recommendationsHistory") ?? [];

  return {
    latestSuicidality: latestSuicidality ? mapSuicidalityFindingFromApi(latestSuicidality) : null,
    latestRisk: latestRisk ? mapRiskFindingFromApi(latestRisk) : null,
    latestMedication: latestMedication ? mapMedicationFindingFromApi(latestMedication) : null,
    latestRecommendations: latestRecommendations ? mapRecommendationsFindingFromApi(latestRecommendations) : null,
    suicidalityHistory: Array.isArray(suicidalityHistory) ? suicidalityHistory.map(mapSuicidalityFindingFromApi) : [],
    riskHistory: Array.isArray(riskHistory) ? riskHistory.map(mapRiskFindingFromApi) : [],
    medicationHistory: Array.isArray(medicationHistory) ? medicationHistory.map(mapMedicationFindingFromApi) : [],
    recommendationsHistory: Array.isArray(recommendationsHistory) ? recommendationsHistory.map(mapRecommendationsFindingFromApi) : [],
  };
}

export type TGoogleCalendarStatus = {
  isConnected: boolean;
  email?: string;
};

export type TGoogleCalendarAuthResponse = {
  success: boolean;
  authUrl?: string;
  userId?: string;
  error?: string;
};

export type TGoogleCalendarAuthCallbackResponse = {
  success: boolean;
  message?: string;
  // Additional fields that might be returned on successful auth
  userEmail?: string;
  calendarId?: string;
};

export type TGoogleCalendarDisconnectResponse = {
  success: boolean;
  message?: string;
};

export type TAppointmentsResponse = {
  appointments: TAppointment[];
  googleEvents: any[]; // Google events are merged into appointments array on server
  totalAppointments: number;
  totalGoogleEvents: number;
  month: number;
  year: number;
  therapistId: number;
};

export interface IQuestionnaireCompaniesHeaders {
  id: number;
  companyId: number;
  desc: string;
  isDefault: boolean;
  statusId: number;
  isEnum?: boolean | null;
  hasLinkedProtocols?: boolean;
}

export interface ITreatmentRoomFeatureLink {
  roomId: number;
  featureId: number;
}

export interface ITreatmentRoom {
  id: number;
  name: string;
  capacity: number;
  location: string;
  features: number[] | { id: number; name: string }[];
  treatmentTypeId?: number;
  treatmentName?: string;
  therapyTypeId?: number;
  statusId: number;
  therapyTypeName?: string;
  statusName?: string;
}

export interface ITreatmentGroup {
  id: number;
  groupName: string;
  maxParticipants?: number;
  therapistId?: number;
  statusId: number;
  therapyTypeName?: string;
  therapyTypeId?: number;
  statusName?: string;
  therapistName?: string;
  currentMemberCount?: number;
  availableSlots?: number | null;
  isFull?: boolean;
  members?: ITreatmentGroupMember[];
}

export interface ITreatmentGroupMember {
  id: number;
  treatmentGroupId: number;
  userId: number;
  userFullName?: string;
  userEmail?: string;
  userPhone?: string;
  joinedDate?: string;
}

export interface IRoomFeature {
  id: number;
  label: string;
}

export interface ICompanyDetails {
  id: number;
  name: string;
  nameEn: string | null;
  logoUrl: string | null;
  phone: string | null;
  email: string;
  contactFullName: string | null;
  positionTitle: string | null;
  guid: string;
  companySizeId: number | null;
  sectorId: number | null;
  parentCompanyId: number | null;
  startQuestionnaireCompaniesTypeId: number | null;
  trialDays: number | null;
  statusId: number;
  imageUrl: string | null;
  prefferedLanguageId: number | null;
  createdAt: string | null;
  isChargingCustomers: boolean;
  companyChargeTriggersId: number | null;
  therapistsCanSeePrices?: boolean;
  canAddDefaultReports: boolean | null;
}

// Therapist Questions Reports Types
export type TTherapistQuestionReport = {
  Id: number;
  AppointmentsId: number | null;
  AppointmentDate: string | null;
  QuestionnaireCompaniesScoreUsersId: number;
  TherapistName: string;
  ReporterName?: string;
  TherapistId: number;
  TherapistsGuid: string;
  UserId: number;
  UsersGuid: string;
  UserFirstName: string;
  UserLastName: string;
  UserPhone?: string;
  UserBirth?: string;
  Timestamp: string;
  StatusId: number;
  StatusName: string;
  Cycle: number;
  QuestionnaireName: string;
  QuestionnaireId: number;
  QuestionnaireCompaniesId: number;
  QuestionnaireCompaniesTypeId: number;
  Score?: number;
  RRM?: number;
  TotalAmount?: number | null;
  TotalAmountPay?: number | null;
  HasTherapistAnswered: boolean;
};

export type TTherapistsQuestionsReportsResponse = TTherapistQuestionReport[];

// Type for getting questionnaire answers for editing
export type TGetQuestionnaireAnswersForEditResponse = {
  QuestionnaireCompaniesScoreUsersId: number;
  Cycle?: number;
  QuestionnaireId?: number;
  QuestionnaireCompaniesId?: number;
  DefaultName?: string | null;
  TotalAmount?: number | null;
  TotalAmountPay?: number | null;
  CompanyServiceId?: number | null;
  QuestionnaireCompaniesScoreUsersStatusesId?: number | null;
  Questions: Array<{
    QuestionId: number;
    DataTypesId: number;
    Answer?: string | null;  // For text/number/date
    SelectedOptions?: Array<{
      OptionId: number;
      GroupId: number | null;
    }>;  // For select fields with group info
    CheckboxValue?: boolean;  // For checkbox
  }>;
};

// Structured Meeting Summary Types
export interface TPatientStatus {
  currentCondition: string;
  progressNotes: string;
  concerns: string;
}

export interface TTreatmentPlan {
  current: string;
  modifications: string;
  nextSteps: string;
}

export interface TStructuredMeetingSummary {
  subject: string;
  primaryFindings: string[];
  decisions: string[];
  recommendations: string[];
  actionItems: string[];
  patientStatus: TPatientStatus;
  treatmentPlan: TTreatmentPlan;
  additionalNotes: string;
  nextAppointment: string;
}
// Auto-fill Action Report Types
export interface TTherapistQuestionnaireForAutoFill {
  questionnaireId: number;
  questionnaireCompaniesId: number;
  /** Every component this questionnaire is linked to. Missing on older API versions. */
  questionnaireCompaniesIds?: number[];
  name: string;
  typeName?: string | null;
  symbol?: string | null;
}

export interface TAutoFillActionReportRequest {
  QuestionnaireId: number;
  MeetingSummary: string;
  Transcript?: string;
  Language?: string;
}

export interface TAutoFilledAnswer {
  questionId: number;
  dataTypeId: number;
  textAnswer?: string | null;
  selectedOptionIds: number[];
  confidence?: number | null;
}

export interface TAutoFillActionReportResponse {
  success: boolean;
  message: string;
  answers: TAutoFilledAnswer[];
  rawAiResponse?: string;
}

export type TFeatureKey =
  | "SMART_CALENDAR"
  | "ONLINE_INTAKE"
  | "SESSION_DOCUMENTATION"
  | "TREATMENT_PLANS"
  | "DOCUMENT_MANAGEMENT"
  | "QUESTIONNAIRE_BUILDER"
  | "PROGRESS_TRACKING"
  | "THERAPIST_PATIENT_ASSIGNMENT"
  | "EXERCISES"
  | "FORUM"
  | "EXPORT_PDF_EXCEL"
  | "SECURE_CHAT"
  | "VIDEO_CALLS"
  | "AUTOMATIC_REMINDERS"
  | "SYSTEM_UPDATES"
  | "APPOINTMENT_SCHEDULING"
  | "SMART_INTAKE_VENCI"
  | "AI_COMPANION_24_7"
  | "AUTO_REPORT_SUMMARY"
  | "SESSION_RECORDING"
  | "NARRATIVE_REPORTS"
  | "CLINICAL_SUMMARY"
  | "VENCI_CONSULTATION"
  | "ACTIVITY_REPORT_ANALYSIS"
  | "BILLING_SYSTEM"
  | "INVOICE_GENERATION"
  | "GOOGLE_CALENDAR_SYNC"
  | "TECHNICAL_SUPPORT"
  | "PREMIUM_SUPPORT"
  | "CLINICAL_ANALYTICS"
  | "ALERT_HOTLINE"
  | "MISSING_REPORT_ALERTS"
  | "TEAM_PERMISSIONS"
  | "TREATMENT_ROOMS"
  | "PERIODIC_REPORTS"
  | "CLOUD_PBX"
  | "PRESCRIPTION_BASIC"
  | "PRESCRIPTION_AUTO";

export interface EntitlementsFeatureUsageDto {
  feature: TFeatureKey;
  companyLimit: number | null;
  companyUsed: number;
  userLimit: number | null;
  userUsed: number;
  trialReduced: boolean;
  trialActive: boolean;
  resetsAt: string | null;
  isResourceCap: boolean;
}

export type TClientFile = {
  fileName: string;
  subject: string;
  urgency: string;
  details: string;
  dateUploaded: string;
};

export interface IGetUserEntitlementsResponse {
  planName: string;
  trial: {
    active: boolean;
    endsAt: string | null;
  };
  features: TFeatureKey[];
  aiQuota: {
    company: {
      limit: number;
      used: number;
      resetsAt: string;
      trialReduced: boolean;
      trialActive: boolean;
    };
    user: {
      limit: number;
      used: number;
      resetsAt: string;
    };
  };
  featureUsage: EntitlementsFeatureUsageDto[];
}

/**
 * What the hotline questionnaire rule says this patient still owes, if anything.
 *
 * `pending` is null for almost every patient almost always — that is the normal answer, not an
 * error state, and the UI should render nothing rather than an empty banner.
 */
export interface IPendingHotlineQuestionnaire {
  /** The questionnaire's own name, as the admin configured it. */
  name: string;
  /** Why it is due: "ReturningCaller" | "Periodic" | "LastContact". Decides the wording. */
  reason: string;
  /** The message stage whose wording matches this reason. */
  stage: number;
  /**
   * The final session of a course, where the spec wants the questionnaire filled BEFORE the
   * contact rather than by the end of it. The banner says so more firmly.
   */
  isLastContact: boolean;
  /**
   * Where to open the questionnaire and fill it in with the patient.
   *
   * The same anonymous link the patient is texted -- the guid is the credential the questionnaire
   * page already takes, and the answers land against the patient either way. Null when the server
   * could not build one, and the banner then simply has no button.
   */
  link: string | null;
}

export interface IPendingHotlineQuestionnaireResponse {
  success: boolean;
  pending: IPendingHotlineQuestionnaire | null;
}
